// Modified ProjectDock component to fix the circular update issue
import Addicon from "@mui/icons-material/Add";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { InfoContainer, Popin, Text } from "$components";
import CField, { CFieldInputProps } from "$forms/CField";
import DockyardSelect from "$forms/DockyardSelect";
import ProjectSelect from "$forms/ProjectSelect";
import { appState } from "$reducers";
import { publicIconPath } from "$utils/constants";
import DockyardForm from "../../../Dockyard/DockyardForm";
import ProjectForm from "../../../Projects/ProjectForm";
import AdminCreateButton from "../components/AdminCreateButton";
import { useEDBFormContext } from "../context/EDBFormContext";
import useProjectDockPicker from "../hooks/useProjectDockPicker";
import { useStyles } from "../styles/EDBForm.styles";

export default function ProjectDock() {
  const classes = useStyles();

  const { setProjectOptions, setDockyardOptions, sourceDockyardCompany } =
    useEDBFormContext();
  const { filter: sidebarCompanyFilter } = useSelector(appState);

  // This state controls what is displayed in the form
  // Rather than relying on form state which can cause loops
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedDockyardId, setSelectedDockyardId] = useState(null);

  // Store the actual objects for references
  const [projectObject, setProjectObject] = useState(null);
  const [dockyardObject, setDockyardObject] = useState(null);

  // Flag to prevent circular updates
  const preventCircularUpdates = useRef(false);

  const {
    defaultValuesDockyardForm,
    setDefaultValuesDockyardForm,

    dockClass,
    errorProject,
    errorDockyard,
    isOpenedProject,
    isDockyardPopinOpened,
    submittingProject,
    submittingDockyard,

    handleCreateProject,
    handleCreateDockyard,
    openProjectPopin,
    openDockyardPopin,
    toggleDockPopin,
    togglePopinProject,
  } = useProjectDockPicker();

  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext();

  const watchedProject = watch("project");
  const watchedDockyard = watch("dockyard");
  const watchedCompany = watch("company");

  useEffect(() => {
    if (watchedProject && watchedProject !== selectedProjectId) {
      setSelectedProjectId(watchedProject);
    }
  }, [watchedProject, selectedProjectId]);

  useEffect(() => {
    if (watchedDockyard && watchedDockyard !== selectedDockyardId) {
      setSelectedDockyardId(watchedDockyard);
    }
  }, [watchedDockyard, selectedDockyardId]);

  // Sync defaultValuesDockyardForm with current form values
  // This handles the case when EDB is created from a dockyard and project is pre-filled
  useEffect(() => {
    if (watchedProject && watchedCompany) {
      setDefaultValuesDockyardForm({
        company: watchedCompany,
        project: watchedProject,
      });
    } else if (!watchedProject) {
      setDefaultValuesDockyardForm({});
    }
  }, [watchedProject, watchedCompany, setDefaultValuesDockyardForm]);

  // Custom project change handler that manages form state
  const handleProjectChange = (value, projectObj) => {
    // Update our local state
    setSelectedProjectId(value);
    setProjectObject(projectObj);

    // Update form values in a controlled way
    if (value) {
      if (preventCircularUpdates.current) return;

      preventCircularUpdates.current = true;

      // Get company from project
      const companyId = projectObj?.company?._id || projectObj?.company;
      setValue("company", companyId);
      setValue("project", value);

      // Update default values for dockyard form
      setDefaultValuesDockyardForm({
        company: companyId,
        project: value,
      });

      // Clear dockyard if it doesn't match the selected project
      if (selectedDockyardId && dockyardObject) {
        const dockyardProjectId =
          dockyardObject?.project?._id || dockyardObject?.project;

        if (dockyardProjectId !== value) {
          setValue("dockyard", null);
          setSelectedDockyardId(null);
          setDockyardObject(null);
        }
      }

      // Reset values
      setValue("intervenantCompany", null);
      setValue("intervenantContact", null);

      setTimeout(() => {
        preventCircularUpdates.current = false;
      }, 100);
    } else {
      // Clear values when no project selected
      setValue("project", null);
      setValue("company", null);
      setValue("dockyard", null);
      setSelectedDockyardId(null);
      setDockyardObject(null);

      // Clear default values for dockyard form
      setDefaultValuesDockyardForm({});
    }
  };

  // Custom dockyard change handler that manages form state
  const handleDockyardChange = (value, dockyardObj) => {
    // Update our local state
    setSelectedDockyardId(value);
    setDockyardObject(dockyardObj);

    // Update form values in a controlled way
    if (value) {
      if (preventCircularUpdates.current) return;

      preventCircularUpdates.current = true;

      setValue("dockyard", value);

      // Get company from dockyard
      const companyId = dockyardObj?.company?._id || dockyardObj?.company;
      setValue("company", companyId);

      // Set project from dockyard, but only if different
      const projectId = dockyardObj?.project?._id || dockyardObj?.project;
      if (projectId && projectId !== selectedProjectId) {
        setValue("project", projectId);
        setSelectedProjectId(projectId);
      }

      // Set address fields
      setValue("dispositionAdress", dockyardObj?.address);
      setValue("dispositionAdress2", dockyardObj?.address2);
      setValue("dispositionPostalCode", dockyardObj?.postalCode);
      setValue("dispositionCity", dockyardObj?.city);
      setValue("dispositionCountry", dockyardObj?.country);

      // Set other fields
      setValue("intervenantCompany", dockyardObj?.interveningCompany?.[0]?._id);
      setValue("intervenantContact", null);

      setTimeout(() => {
        preventCircularUpdates.current = false;
      }, 100);
    } else {
      setValue("dockyard", null);
    }
  };

  // Handle data received from ProjectSelect and DockyardSelect
  const handleProjectDataFetched = useCallback(
    (options) => {
      setProjectOptions(options);

      // If we have a selected project ID but not the object, find it
      if (selectedProjectId && !projectObject) {
        const found = options.find((p) => p._id === selectedProjectId);
        if (found) {
          setProjectObject(found);
        }
      }
    },
    [setProjectOptions, selectedProjectId, projectObject]
  );

  const handleDockyardDataFetched = useCallback(
    (options) => {
      setDockyardOptions(options);

      // If we have a selected dockyard ID but not the object, find it
      if (selectedDockyardId && !dockyardObject) {
        const found = options.find((d) => d._id === selectedDockyardId);
        if (found) {
          setDockyardObject(found);
        }
      }
    },
    [setDockyardOptions, selectedDockyardId, dockyardObject]
  );

  return (
    <>
      <div>
        <InfoContainer
          leftHeader={
            <>
              <img
                alt="ico-batail"
                src={`${publicIconPath}/info-chantier.svg`}
              />
              <Text type="h3" className={classes.title}>
                Identification du projet et du chantier
              </Text>
            </>
          }
        >
          <div className={classes.step1From}>
            <div className={classes.projects}>
              <CField
                control={control}
                name="project"
                error={errors.project}
                style={{ marginRight: 4 }}
              >
                {(props: CFieldInputProps) => (
                  <ProjectSelect
                    {...props}
                    className={classes.select}
                    label="Rechercher un projet"
                    onChange={handleProjectChange}
                    onDataFetched={handleProjectDataFetched}
                    companyId={sidebarCompanyFilter?._id}
                    minCharsToTrigerSearch={0}
                    key={`project-select-${selectedProjectId || "empty"}`}
                  />
                )}
              </CField>
              <AdminCreateButton onClick={() => openProjectPopin()}>
                <Addicon />
              </AdminCreateButton>
            </div>
            <div className={classes.chantiers}>
              <CField
                control={control}
                name="dockyard"
                error={errors.dockyard}
                style={{ marginRight: 4 }}
              >
                {(props: CFieldInputProps) => (
                  <DockyardSelect
                    {...props}
                    className={classes.select}
                    label="Rechercher un chantier"
                    project={selectedProjectId}
                    allowNoCompany
                    onChange={handleDockyardChange}
                    onDataFetched={handleDockyardDataFetched}
                    minCharsToTrigerSearch={0}
                    companyId={sidebarCompanyFilter?._id}
                    key={`dockyard-select-${selectedProjectId || "empty"}-${
                      selectedDockyardId || "empty"
                    }`}
                  />
                )}
              </CField>
              <AdminCreateButton onClick={() => openDockyardPopin()}>
                <Addicon />
              </AdminCreateButton>
            </div>
          </div>
        </InfoContainer>
      </div>
      {/* Project drawer */}
      <Popin
        className={dockClass.ProjectsPage_Popin}
        openPopin={isOpenedProject}
        toggleDrawer={togglePopinProject}
      >
        <ProjectForm
          onSubmit={handleCreateProject}
          projectError={errorProject}
          onReset={() => togglePopinProject(false)}
          isSubmitting={submittingProject}
          defaultValuesForm={
            sourceDockyardCompany
              ? { company: sourceDockyardCompany._id }
              : undefined
          }
        />
      </Popin>
      {/* Dockyard drawer */}
      <Popin
        className={dockClass.ProjectsPage_Popin}
        openPopin={isDockyardPopinOpened}
        toggleDrawer={toggleDockPopin}
      >
        <DockyardForm
          key={`dockyard-form-${
            (defaultValuesDockyardForm as any).project || "no-project"
          }-${(defaultValuesDockyardForm as any).company || "no-company"}`}
          error={errorDockyard}
          onSubmit={handleCreateDockyard}
          onCancel={() => toggleDockPopin(false)}
          isSubmitting={submittingDockyard}
          defaultValuesForm={{
            ...defaultValuesDockyardForm,
            ...(sourceDockyardCompany && {
              company: sourceDockyardCompany._id,
            }),
          }}
        />
      </Popin>
    </>
  );
}
